import React from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

import * as geom from "js/core/utilities/geom";
import { DragBlockManagerRollover } from "js/editor/PresentationEditor/DragBlockManager";
import { DragElementManagerRollover } from "js/editor/PresentationEditor/DragElementManager";
import { ControlBarPosition, DOUBLE_CLICK_THRESHOLD_MS } from "common/constants";
import { stopPropagation } from "js/core/utilities/stopPropagation";
import { DragSelectionManagerRollover } from "js/editor/PresentationEditor/DragSelectionManager";

import PresentationEditorController from "./PresentationEditorController";
import { themeColors } from "../../react/sharedStyles";

const Container = styled.div.attrs(({ visible }) => ({
    style: {
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? "default" : "none"
    }
}))`
    width: 100%;
    height: 100%;
    transition: opacity 0.2s ease-in-out;
    position: relative;
`;

const ElementUIContainer = styled.div.attrs(({ bounds, opacity }) => ({
    style: {
        ...bounds.toObject(),
        opacity
    }
}))`
    position: absolute;

    > .drag-handle {
        display: ${props => props.hideHandles ? "none" : "default"};
    }

    > .resize-handle {
        display: ${props => props.hideHandles ? "none" : "default"};
    }
`;

const FauxCanvasContainer = styled.div.attrs(({ canvasBounds }) => ({
    style: {
        ...canvasBounds.toObject()
    }
}))`
    position: absolute;
    pointer-events: none;
`;

const CanvasDimmer = styled.div.attrs(({ canvasBounds, containersBounds }) => ({
    style: {
        ...canvasBounds.toObject(),
        clipPath: [`path(evenodd, "`,
            `M 0 0`,
            `L ${canvasBounds.width} 0`,
            `L ${canvasBounds.width} ${canvasBounds.height}`,
            `L 0 ${canvasBounds.height}`,
            `L 0 0`,
            containersBounds.map(containerBounds => [
                `M ${containerBounds.left} ${containerBounds.top}`,
                `L ${containerBounds.right} ${containerBounds.top}`,
                `L ${containerBounds.right} ${containerBounds.bottom}`,
                `L ${containerBounds.left} ${containerBounds.bottom}`,
                `L ${containerBounds.left} ${containerBounds.top}`,
            ].join(" ")).join(" "),
            `Z`,
            `")`].join(" ")
    }
}))`
    position: absolute;
    pointer-events: none;

    background: rgba(0, 0, 0, 0.4);
    z-index: 999998;
`;

export const NoFitWarning = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: ${props => props.corner ? "flex-start" : "center"};
    justify-content: ${props => props.corner ? "flex-end" : "center"};
    top: 0px;
    left: 0px;
    z-index: 10000;

    > div.backdrop {
        position: absolute;
        width: 100%;
        height: 100%;
        background: rgba(255, 69, 0, 0.25);
        border: solid 1px rgba(255, 69, 0, 0.5);;
    }

    > div.label {
        position: absolute;
        left: 50%;
        bottom: -50px;
        transform: ${props => props.corner ? "translate(-50%, 0)" : null};
        background: ${themeColors.ui_blue};
        color: white;
        text-transform: uppercase;
        font-size: 12px;
        font-weight: bold;
        padding: 6px 10px;
    }

    .bai-icon {
        transform: ${props => props.corner ? "translate(50%, -50%)" : null};
        background: orangered;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        color: white;
        font-size: 14px;
        cursor: pointer;
        pointer-events: auto;
    }
`;

const DefaultOverlayContainer = styled.div.attrs(({ opacity }) => ({
    style: {
        opacity
    }
}))`
    width: 100%;
    height: 100%;
`;

class SelectionLayer extends React.Component {
    get selectionLayerController() {
        return this.props.selectionLayerController;
    }

    get canvasController() {
        return this.props.canvasController;
    }

    get isDraggingElement() {
        return !!this.props.dragElementState;
    }

    get isDraggingBlock() {
        return !!this.props.dragBlockState;
    }

    componentDidMount() {
        window.addEventListener("mousemove", this.handleMouseMove);
        window.addEventListener("mousedown", this.handleMouseDown);
    }

    componentWillUnmount() {
        window.removeEventListener("mousemove", this.handleMouseMove);
        window.removeEventListener("mousedown", this.handleMouseDown);
    }

    shouldComponentUpdate(nextProps) {
        const { isCanvasGenerating: willCanvasBeGenerating, freezeSelectionLayer } = nextProps;

        if (willCanvasBeGenerating) {
            // Prevent refresh before/during canvas generation
            return false;
        }

        if (freezeSelectionLayer) {
            // Prevent refresh when selection layer is frozen
            return false;
        }

        return true;
    }

    handleMouseDown = event => {
        const { isCanvasGenerating, isCanvasAnimating, freezeSelection, selectionLayerController, isCurrentSlideLockedForMe, allowEditing, canvasController } = this.props;
        if (isCanvasGenerating || isCanvasAnimating || freezeSelection || isCurrentSlideLockedForMe || !allowEditing) {
            return;
        }

        // don't select elements when clicking on the help menu
        if (event.target?.closest(".MuiPopover-root")) {
            return;
        }

        const position = new geom.Point(event.clientX, event.clientY);

        let isDoubleClick = false;
        if (selectionLayerController.prevMouseDownAt + DOUBLE_CLICK_THRESHOLD_MS > Date.now()) {
            const distance = position.distance(selectionLayerController.prevMouseDownPosition);
            if (distance < 5) {
                isDoubleClick = true;
            }
        }

        selectionLayerController.prevMouseDownAt = Date.now();
        selectionLayerController.prevMouseDownPosition = position;

        const elements = selectionLayerController.getElementsForSelection(event, isDoubleClick);

        // clear double click detection if we selected a different element on the first click
        // this prevents doubleclickable elements from being selected before a parent element is selected (like a picture in a sidebar)
        if (elements.length && selectionLayerController.selectedElements[0] !== elements[0] && elements[0].preventDirectDoubleClickOnChildren) {
            selectionLayerController.prevMouseDownAt = selectionLayerController.prevMouseDownPosition = null;
        }

        if (!(elements.length == 0 && event.shiftKey)) {
            selectionLayerController.setSelectedElements(elements);
        }

        this.checkIfUserIsTryingToDragElement = new geom.Point(event.clientX, event.clientY);

        if (elements.length === 0 && canvasController.canvas.hasElementsSupportingMultiSelect()) {
            selectionLayerController.registerDragSelection(event);
        }
    }

    handleMouseMove = event => {
        const { isCanvasGenerating, isCanvasAnimating, freezeSelection, selectionLayerController, isCurrentSlideLockedForMe } = this.props;
        if (isCanvasGenerating || isCanvasAnimating || freezeSelection || isCurrentSlideLockedForMe) {
            return;
        }

        if (this.isDraggingElement || this.isDraggingBlock) {
            return;
        }

        const elements = selectionLayerController.getElementsForRollover(event);
        selectionLayerController.setRolloverElements(elements);

        if (event.buttons == 1) {
            if (this.checkIfUserIsTryingToDragElement && this.checkIfUserIsTryingToDragElement.distance(new geom.Point(event.clientX, event.clientY)) > 30) {
                this.checkIfUserIsTryingToDragElement = false;
                for (let element of [...this.props.selectedElements, ...this.props.elementsWithSelection]) {
                    let selectionRef = element.uiRefs.selectionRef;
                    if (selectionRef?.current) {
                        selectionRef.current.userIsTryingToDragElement && selectionRef.current.userIsTryingToDragElement(event);
                    }
                }
            }
        } else {
            // always clear on mousemove with no button press because we arent alwways getting mousedown/mouseup events in selection layer
            this.checkIfUserIsTryingToDragElement = false;
        }
    }

    render() {
        const {
            hilitedElements,
            selectedElements,
            elementsWithSelection,
            rolloverElements,
            canvas,
            canvasController,
            selectionLayerController,
            dragBlockState,
            dragElementState,
            dragSelectionState,
            primaryElement,
            isCanvasAnimating,
            useCanvasDimmerForHilitedElements,
            showSelectionLayer,
            isPanZoomed
        } = this.props;

        if (canvasController.isTemplateObsolete) {
            return null;
        }

        const elementsWithDefaultOverlay = canvas.getElements().filter(element => element.showDefaultOverlay);
        const elementsWithForceShowSelection = canvas.getElements().filter(element => element.forceShowSelection);

        const elements = [...new Set([...selectedElements, ...elementsWithSelection, ...rolloverElements, ...hilitedElements, ...elementsWithDefaultOverlay, ...elementsWithForceShowSelection, primaryElement])]
            .filter(Boolean)
            .filter(element => element.isCalculated)
            .filter(element => !element.isDeleted)
            .sort((a, b) => a.getElementPath().length - b.getElementPath().length);

        const elementsWithUI = elements.map(element => {
            const isPrimaryElement = element === primaryElement;
            const showSelection = selectedElements.includes(element) || elementsWithSelection.includes(element) || element.forceShowSelection;
            const showControlBar = showSelection || isPrimaryElement;
            const isRollover = rolloverElements.includes(element);
            const isHilited = hilitedElements.includes(element) && !useCanvasDimmerForHilitedElements;
            const showDefaultOverlay = elementsWithDefaultOverlay.includes(element);
            const multiSelectionGroupId = element.multiSelectionGroupId;
            const canMultiSelect = element.canMultiSelect;
            const isPartOfMultiSelect = false;

            const Selection = showSelection ? element.getElementSelection() : null;
            const Rollover = (isRollover || isHilited) ? element.getElementRollover() : null;
            const DefaultOverlay = showDefaultOverlay ? element.getElementDefaultOverlay() : null;

            let ControlBar;
            if (showControlBar) {
                ControlBar = element.getElementControlBar();
                if (ControlBar === null) {
                    ControlBar = element.getDefaultControlBar();
                }
            }

            return {
                element,
                isPrimaryElement,
                isHilited,
                isRollover,
                multiSelectionGroupId,
                canMultiSelect,
                isPartOfMultiSelect,
                Selection,
                Rollover,
                DefaultOverlay,
                ControlBar,
            };
        });

        elementsWithUI.forEach(element => {
            if (
                element.canMultiSelect &&
                selectedElements.includes(element.element) &&
                elementsWithUI.some(e =>
                    e.multiSelectionGroupId === element.multiSelectionGroupId &&
                    e.canMultiSelect &&
                    e.element !== element.element &&
                    selectedElements.includes(e.element)
                )
            ) {
                element.isPartOfMultiSelect = true;
            }
        });

        const hasCanvasControlBar = elementsWithUI.some(element => !element.isPrimaryElement && (element.ControlBar?.prototype?.alwaysPositionBelowCanvas || element.ControlBar?.prototype?.suppressPrimaryControlBar));
        if (hasCanvasControlBar) {
            elementsWithUI.forEach(elementUI => {
                if (elementUI.isPrimaryElement) {
                    elementUI.ControlBar = null;
                }
            });
        }

        let Dimmer;
        if (useCanvasDimmerForHilitedElements && hilitedElements.length > 0) {
            const canvasScreenBounds = canvasController.canvasScreenBounds;
            const containersBounds = hilitedElements.map(element => element.selectionBounds.spaceScale(canvas.getScale()));
            Dimmer = <CanvasDimmer canvasBounds={canvasScreenBounds.zeroOffset()} containersBounds={containersBounds} />;
        }

        const multiSelectGroups = {};
        elementsWithUI.forEach(element => {
            if (element.isPartOfMultiSelect) {
                if (!multiSelectGroups[element.multiSelectionGroupId]) {
                    multiSelectGroups[element.multiSelectionGroupId] = {
                        ...element,
                        element: null,
                        elements: [element.element],
                    };
                } else {
                    multiSelectGroups[element.multiSelectionGroupId].elements.push(element.element);
                }
            }
        });

        return (<Container id="selection-layer-container" visible={!isCanvasAnimating}>
            {/* Element UI (non-multi-selected elements) */}
            {elementsWithUI.map(({
                element,
                isPrimaryElement,
                isHilited,
                isRollover,
                isPartOfMultiSelect,
                Selection,
                Rollover,
                DefaultOverlay,
                ControlBar
            }) => {
                if (!Selection && !Rollover && !DefaultOverlay && !ControlBar && !isHilited) {
                    return null;
                }

                if (isPartOfMultiSelect) {
                    Selection = null;
                    ControlBar = null;
                }

                const isBeingDragged = element.isBeingDragged || element.isParentBeingDragged;
                const isBeingResized = element.isBeingResized || element.isParentBeingResized;

                const containerOpacity = showSelectionLayer ? (isBeingDragged ? selectionLayerController.dragElementOpacity : 1) : 0;

                const containerBounds = element.selectionBounds.spaceScale(canvas.getScale());

                // Canvas bounds relative to the element selection bounds to let the editor know where the canvas is
                const canvasBounds = new geom.Rect(0, 0, canvas.CANVAS_WIDTH, canvas.CANVAS_HEIGHT)
                    .setPosition(-element.selectionBounds.left, -element.selectionBounds.top)
                    .spaceScale(canvas.getScale());

                const selectionProps = {
                    ...this.props,
                    element,
                    elements: [element],
                    canvasBounds,
                    containerBounds,
                    isBeingDragged,
                    isBeingResized,
                    hasRollover: !!Rollover,
                    hasSelection: !!Selection,
                    hasDefaultOverlay: !!DefaultOverlay,
                    hasControlBar: !!ControlBar
                };

                const {
                    containerRef,
                    defaultOverlayRef,
                    selectionRef,
                    rolloverRef,
                    controlBarRef
                } = element.uiRefs;

                return (
                    <React.Fragment key={element.uniquePath}>
                        {DefaultOverlay && element.defaultOverlayContainerRef.current && createPortal(
                            <DefaultOverlayContainer opacity={containerOpacity}>
                                <DefaultOverlay {...selectionProps} ref={defaultOverlayRef} />
                            </DefaultOverlayContainer>,
                            element.defaultOverlayContainerRef.current
                        )}
                        <ElementUIContainer
                            bounds={containerBounds}
                            onMouseDown={stopPropagation()}
                            ref={containerRef}
                            opacity={containerOpacity}
                            hideHandles={isBeingDragged}
                            data-element-path={element.uniquePath}
                            data-element-type={element.type}
                        >
                            {Selection && <Selection {...selectionProps} ref={selectionRef} />}

                            {(isRollover || isHilited) && Rollover && <Rollover {...selectionProps} ref={rolloverRef} />}

                            {ControlBar && !isBeingDragged && !isBeingResized && (
                                <>
                                    {(isPrimaryElement || ControlBar.prototype.alwaysPositionBelowCanvas) && !isPanZoomed && (
                                        <FauxCanvasContainer canvasBounds={canvasBounds}>
                                            <ControlBar {...selectionProps} position={ControlBarPosition.CANVAS} ref={controlBarRef} />
                                        </FauxCanvasContainer>
                                    )}
                                    {!isPrimaryElement && !ControlBar.prototype.alwaysPositionBelowCanvas &&
                                        <ControlBar {...selectionProps} ref={controlBarRef} />
                                    }
                                </>
                            )}
                        </ElementUIContainer>
                    </React.Fragment>
                );
            })}
            {/* Element UI (multi-selected elements) */}
            {Object.values(multiSelectGroups).map(({
                elements,
                Selection,
                ControlBar,
                multiSelectionGroupId
            }) => {
                if (!Selection?.allowsMultiSelect) {
                    Selection = null;
                }
                if (!ControlBar?.allowsMultiSelect) {
                    ControlBar = null;
                }

                if (!Selection && !ControlBar) {
                    return null;
                }

                const isBeingDragged = elements.some(element => element.isBeingDragged || element.isParentBeingDragged);
                const isBeingResized = elements.some(element => element.isBeingResized || element.isParentBeingResized);

                const containerOpacity = showSelectionLayer ? (isBeingDragged ? selectionLayerController.dragElementOpacity : 1) : 0;

                const selectionBounds = elements.reduce((bounds, element) => bounds ? bounds.union(element.selectionBounds) : element.selectionBounds, null);
                const containerBounds = selectionBounds.spaceScale(canvas.getScale());

                // Canvas bounds relative to the element selection bounds to let the editor know where the canvas is
                const canvasBounds = new geom.Rect(0, 0, canvas.CANVAS_WIDTH, canvas.CANVAS_HEIGHT)
                    .setPosition(-selectionBounds.left, -selectionBounds.top)
                    .spaceScale(canvas.getScale());

                const selectionProps = {
                    ...this.props,
                    element: null,
                    elements,
                    canvasBounds,
                    containerBounds,
                    isBeingDragged,
                    isBeingResized,
                    hasRollover: false,
                    hasSelection: !!Selection,
                    hasDefaultOverlay: false,
                    hasControlBar: !!ControlBar,
                };

                return (
                    <React.Fragment key={`multi-select-group-${multiSelectionGroupId}`}>
                        <ElementUIContainer
                            bounds={containerBounds}
                            onMouseDown={stopPropagation()}
                            opacity={containerOpacity}
                            hideHandles={isBeingDragged}
                            data-element-path={`multi-select-group-${multiSelectionGroupId}`}
                            data-element-type={elements[0].type}
                        >
                            {Selection && <Selection {...selectionProps} />}

                            {ControlBar && !isBeingDragged && !isBeingResized && (
                                <>
                                    {ControlBar.prototype.alwaysPositionBelowCanvas && !isPanZoomed && (
                                        <FauxCanvasContainer canvasBounds={canvasBounds}>
                                            <ControlBar {...selectionProps} position={ControlBarPosition.CANVAS} />
                                        </FauxCanvasContainer>
                                    )}
                                    {!ControlBar.prototype.alwaysPositionBelowCanvas &&
                                        <ControlBar {...selectionProps} />
                                    }
                                </>
                            )}
                        </ElementUIContainer>
                    </React.Fragment>
                );
            })}
            {/* Canvas dimmer for hilited elements */}
            {Dimmer}
            {/* Drag managers */}
            <DragBlockManagerRollover
                canvasController={canvasController}
                selectionLayerController={selectionLayerController}
                dragState={dragBlockState}
            />
            <DragElementManagerRollover
                canvasController={canvasController}
                selectionLayerController={selectionLayerController}
                dragState={dragElementState}
            />
            <DragSelectionManagerRollover
                canvasController={canvasController}
                selectionLayerController={selectionLayerController}
                dragState={dragSelectionState}
            />
        </Container>);
    }
}

const SelectionLayerWrapper = React.forwardRef((props, ref) => {
    const { isCanvasRendered, canvas, isSingleSlideEditor } = props;
    if (!isCanvasRendered) {
        return null;
    }

    if (!canvas.isCurrentCanvas && !isSingleSlideEditor) {
        return null;
    }

    return <SelectionLayer {...props} ref={ref} />;
});

export default PresentationEditorController.withFullState(SelectionLayerWrapper);
